import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { TrashIcon } from '../../../../../../design/static/icons/TrashIcon';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { EnableFor } from '../../../../../containers/EnableForRole';
import { ToolDeleteStrategy } from '../../../projectDetails.models';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { IProjectDetailsStore } from '../../../projectDetails.store';
import { mapToolType, ToolType } from '../helpers/tools.helpers';
import { IToolAffect, IToolUsage } from '../projectDetailsTools.models';
import { AnnotationDiscardConfirmModal } from './AnnotationDiscardConfirmModal';
import { ToolDeleteModal } from './ToolDeleteModal';

interface QuestionDeleteProps {
  isAttribute: boolean;
  isLoading: boolean;
  showModal: boolean;
  showDiscardConfirmModal: boolean;
  toolUsage?: IToolUsage;
  strategy: ToolDeleteStrategy;
  onDelete: () => Promise<void>;
  onCancel: () => void;
  onDeleteConfirmed: () => Promise<void>;
  onStrategyChange: (strategy: ToolDeleteStrategy) => void;
  onCancelDiscard: () => void;
  onConfirmDiscard: () => Promise<void>;
  toolAffect?: IToolAffect;
}

export const QuestionDelete = observer((props: QuestionDeleteProps): ReactElement => {
  const projectDetailsPermissions = useProjectDetailsPermissions();
  const projectDetailsStore = useInjection(IProjectDetailsStore.$);
  const canEdit = projectDetailsPermissions.canEditProject(projectDetailsStore.status);
  const { t } = useTranslator();

  return (
    <>
      <EnableFor hasPermission={canEdit}>
        <IconButton
          size='sm'
          onClick={() => {
            void props.onDelete();
          }}
          loading={props.isLoading}
          disabled={props.isLoading}
          title={t(props.isAttribute ? 'remove_attribute' : 'remove_question')}
        >
          <TrashIcon />
        </IconButton>
      </EnableFor>
      {props.toolUsage && props.toolUsage.occurrenceCount > 0 && (
        <ToolDeleteModal
          strategy={props.strategy}
          showModal={props.showModal}
          onConfirm={props.onDeleteConfirmed}
          onCancel={props.onCancel}
          onStrategyChange={props.onStrategyChange}
          toolType={mapToolType(props.isAttribute ? ToolType.Attribute : ToolType.Question)}
          annotationsCount={props.toolUsage.imageCount || 0}
          objectsCount={props.toolUsage.occurrenceCount || 0}
        />
      )}
      {props.toolUsage && props.toolUsage.occurrenceCount === 0 && (
        <ConfirmationModal
          confirmationHeader={'confirm_delete'}
          confirmationQuestion={props.isAttribute ? 'are_you_sure_you_want_to_delete_attribute' : 'are_you_sure_you_want_to_delete'}
          onConfirm={props.onDeleteConfirmed}
          onCancel={props.onCancel}
          showModal={props.showModal}
          confirmationYes={'delete'}
          loaderKey='tool-delete-confirm-button'
        />
      )}
      <AnnotationDiscardConfirmModal
        onCancel={props.onCancelDiscard}
        onConfirm={props.onConfirmDiscard}
        showModal={props.showDiscardConfirmModal}
        annotationsCount={props.toolUsage?.imageCount || 0}
        objectsCount={props.toolAffect?.objectsCount || 0}
        questionsCount={props.toolAffect?.questionsCount || 0}
        attributesCount={props.toolAffect?.attributesCount || 0}
      />
    </>
  );
});
